import {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  useId
} from 'react';
import { useApolloClient } from '@apollo/client';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';

export const SubscriptionErrors = Object.freeze({
  CONNECT_TIMEOUT: 'AMQJSC0001E', // {code:1, text:"AMQJSC0001E Connect timed out."}
  SUBSCRIBE_TIMEOUT: 'AMQJS0002E', // {code:2, text:"AMQJS0002E Subscribe timed out."}
  SOCKET_ERROR: 'AMQJS0007E', // {code:7, text:"AMQJS0007E Socket error:{0}."}
  SOCKET_CLOSE: 'AMQJS0008I', // {code:8, text:"AMQJS0008I Socket closed."}
  SUBSCRIPTION_HANDSHAKE: 'Error: Error during subscription handshake', //  Error during subscription handshake
  UNKNOWN: 'subscriptionErrorCauseUnknown'
});

export const LOG = LoggerFactory.create(
  'VAH-CustomSubscription',
  {},
  { withBrowserData: true }
);
const SUBSCRIPTION_RECOVERY_TIMEOUT_SECONDS = 60;
const SUBSCRIPTION_RETRY_TIMEOUT_SECONDS = 3;
const SUBSCRIPTION_RECOVERY_ATTEMPT = 3;

/**
 * Return the type of error
 * @param {*} error Error message to pass to the error logger
 */
const getErrorType = error => {
  if (!error || typeof error === 'object') {
    return SubscriptionErrors.UNKNOWN;
  }
  if (error.includes(SubscriptionErrors.SOCKET_ERROR)) {
    return SubscriptionErrors.SOCKET_ERROR;
  }
  if (error.includes(SubscriptionErrors.SOCKET_CLOSE)) {
    return SubscriptionErrors.SOCKET_CLOSE;
  }
  if (error.includes(SubscriptionErrors.CONNECT_TIMEOUT)) {
    return SubscriptionErrors.CONNECT_TIMEOUT;
  }
  if (error.includes(SubscriptionErrors.SUBSCRIBE_TIMEOUT)) {
    return SubscriptionErrors.SUBSCRIBE_TIMEOUT;
  }
  return SubscriptionErrors.UNKNOWN;
};

const parseError = error => {
  // Check for graph errors
  if (error?.graphQLErrors?.length > 0 || error?.networkError?.length > 0) {
    return SubscriptionErrors.SUBSCRIPTION_HANDSHAKE;
  }
  const errorMessage =
    error.errorMessage || error.message || error.type || error;
  return getErrorType(errorMessage);
};

/**
 * Collects the subscription information to use in logs
 *
 * @param options Object containing the variables sent in the subscription
 */
const useSubscriptionInfo = options => {
  const { variables, query } = options;
  const { definitions } = query;
  const definition = definitions?.[0]?.name?.value;

  return useMemo(() => {
    return JSON.stringify({
      definition,
      variables
    });
  }, [definition, variables]);
};

/**
 * This custom subscription is a wrapper that uses the apollo client directly to be
 * able to handle subscription errors including web socket failures with support for
 * re-subscribing whenever a socket connection failure is encountered
 *
 * @deprecated Use useSubscription from @apollo/client instead
 *
 * @param options            The options used to subscribe { query, variables }
 * @param skip               Whether or not this subscription should be skipped
 * @param isOnline           Whether or not the user is online or not
 * @param onSubscriptionData Callback to return data to the consumer when the subscription is completed
 * @param fallbackFn         A function to handle subscription errors that were not recovered
 * @param refetchQuery       refetch function used to pull for the latest data after recovering connection
 * @param recoveryTimeOut    The time we should wait to recover a subscription
 * @param recoveryAttempts   The number of re-tries to recover a subscription
 *
 * Note: Please consider the following pointers when using this hook :
 *
 * - options should be optimized with useMemo
 * - onSubscriptionData should be optimized with useCallback
 */
export const useVAHSubscription = (
  options,
  skip = false,
  isOnline = true,
  onSubscriptionData,
  fallbackFn,
  refetchQuery = () => null,
  recoveryAttempts = SUBSCRIPTION_RECOVERY_ATTEMPT,
  recoveryTimeOut = SUBSCRIPTION_RECOVERY_TIMEOUT_SECONDS
) => {
  const client = useApolloClient();
  const subscriptionInfo = useSubscriptionInfo(options);
  const contextId = useId();
  const subscription = useRef();
  const errorDate = useRef(null);
  const clientRef = useRef();
  const onSubscriptionDataRef = useRef();
  const fallbackFnRef = useRef();
  const isSubscribing = useRef(false);
  const [errorToRecover, setErrorToRecover] = useState(null);

  onSubscriptionDataRef.current = onSubscriptionData;
  fallbackFnRef.current = fallbackFn;
  clientRef.current = client;

  /**
   * function to subscribe live updates and handle errors
   */
  const subscribe = useCallback(() => {
    LOG.info(
      `Subscribe on ${options?.query?.definitions?.[0]?.name?.value}.`,
      subscriptionInfo,
      { contextId }
    );
    isSubscribing.current = true;
    subscription.current = clientRef.current
      .subscribe({ fetchPolicy: 'no-cache', errorPolicy: 'all', ...options })
      .subscribe({
        next: response => {
          const data = (response && response.data) || null;
          onSubscriptionDataRef.current(data, clientRef.current, options);
        },
        error: error => {
          isSubscribing.current = false;
          LOG.error(
            `Unsubscribe from ${options?.query?.definitions?.[0]?.name?.value} with error.`,
            subscriptionInfo,
            { contextId },
            error
          );
          subscription.current.unsubscribe();
          subscription.current = null;

          const errorType = parseError(error);
          const attempt = errorDate.current?.attempt || 0;
          const time = new Date();
          errorDate.current = {
            attempt: attempt + 1,
            time,
            error
          };
          setErrorToRecover(`${errorType}::${attempt + 1}`);
        }
      });
  }, [setErrorToRecover, options]);

  /**
   * Unsubscribe and clean up the subscription whenever options change so
   * we can subscribe with the new values
   */
  useEffect(() => {
    if (subscription.current) {
      LOG.info(
        `Unsubscribe from ${options?.query?.definitions?.[0]?.name?.value}`,
        subscriptionInfo,
        { contextId }
      );
      subscription.current.unsubscribe();
      subscription.current = null;
      isSubscribing.current = false;
      if (!skip) {
        subscribe();
      }
    }
  }, [options, skip, subscribe]);

  /**
   * Effect to subscribe on component mount
   */
  useEffect(() => {
    if (subscription.current || skip || !isOnline || isSubscribing.current) {
      return;
    }
    subscribe();
    return () => {
      if (subscription.current) {
        LOG.info(
          `Unsubscribe from ${options?.query?.definitions?.[0]?.name?.value}`,
          subscriptionInfo,
          { contextId }
        );
        subscription.current.unsubscribe();
      }
    };
  }, [isOnline, skip, subscribe]);

  /**
   * Effect to re-subscribe whenever there is an error to recover and
   * check the number of retries to determine if the fallback function
   * provided should be executed
   */
  useEffect(() => {
    let timeout;
    /**
     * Function to call the fallback function provided by the consumer
     */
    const subscriptionFallback = async (error, errorType, attempt) => {
      // We did not recover from the subs. error
      const subscriptionName = options?.query?.definitions?.[0]?.name?.value;
      const errorDetails = {
        errorType,
        attempt,
        error,
        subscriptionInfo
      };
      LOG.error(
        `Subscription Error occurred for ${subscriptionName}`,
        errorDetails,
        { contextId }
      );

      if (fallbackFnRef.current) {
        // Use the fallback function provided so consumers can provide a custom mechanism for this subscription
        const e = errorType.split('::')[0];
        const resubscribe = await fallbackFnRef.current(e, attempt, options);
        if (resubscribe) {
          isSubscribing.current = false;
          errorDate.current = null;
          subscribe();
        }
      }
    };

    if (errorToRecover && !isSubscribing.current) {
      const { time, attempt, error } = errorDate.current;
      const now = new Date();
      const seconds = (now.getTime() - time.getTime()) / 1000;
      // it's been more than 1 minute since the error was first found, so
      // set the unrecoverable error and run the fallback mechanism provided
      if (seconds > recoveryTimeOut || attempt >= recoveryAttempts) {
        // Run fallback
        subscriptionFallback(error, errorToRecover, attempt);
      } else {
        // Try again in SUBSCRIPTION_RETRY_TIMEOUT_SECONDS
        timeout = setTimeout(() => {
          subscribe();
        }, SUBSCRIPTION_RETRY_TIMEOUT_SECONDS * 1000);
      }
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [
    errorToRecover,
    subscribe,
    subscriptionInfo,
    options,
    recoveryAttempts,
    recoveryTimeOut
  ]);

  // Have we recovered from an error ?
  if (subscription.current && errorToRecover) {
    isSubscribing.current = false;
    errorDate.current = null;
    isSubscribing.current = false;
    LOG.info(
      `Connection re-established for >>> ${options?.query?.definitions?.[0]?.name?.value}`,
      subscriptionInfo,
      { contextId }
    );
    // User was disconnected and might not have the latest data. Re-fetching when
    // re-establishing the socket to guarantee the user is up to date with the data
    refetchQuery();
    setErrorToRecover(null);
  }
};
